/** The time a game's scan is assumed to take until this session has timed some. */
export const DEFAULT_SECONDS_PER_GAME = 20;

/** Who queued a game: the automatic scan, or a plugin's own queue (the opening history's). */
export type ScanSource = string & { readonly __brand: 'ScanSource' };

/** Builds a `ScanSource`; refuses an empty name. */
export function scanSource(name: string): ScanSource {
  if (!name) throw new Error('a scan source needs a name');
  return name as ScanSource;
}

/** One game's place in the queue; undefined when it is not queued. */
export type GameScanState = 'queued' | 'scanning' | 'failed';

/** The queue as a whole: one engine, one list. */
export interface ScanProgress {
  running: boolean;
  /** Games finished in the current batch, and the batch's size. */
  done: number;
  total: number;
  /** The game being scanned. */
  current?: string;
  /** Stop was asked; the game in progress finishes first. */
  stopping: boolean;
}

export interface ScanService {
  progress(): ScanProgress;
  /** Hears every change: progress, a game's state, a scan completed. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
  /**
   * The automatic scan: queues every game of the last 30 days without a
   * complete scan, newest first, skipping the queued and the failed (a failed
   * game waits for the next app load). Calls made while one is reading
   * coalesce into one more pass; resolves when the games are queued.
   */
  scanAll(): Promise<void>;
  /** Queues these games at the back, skipping the scanned and the queued. */
  enqueue(ids: readonly string[], source: ScanSource): void;
  /** Moves a game to the front, queuing it if it was not. */
  prioritize(id: string, source: ScanSource): void;
  state(id: string): GameScanState | undefined;
  /** Holds back a source's waiting games (the history when its review is left); others run on. */
  pause(source: ScanSource): void;
  resume(source: ScanSource): void;
  /** Stops after the game in progress and drops every waiting game. */
  stop(): void;
  /** Forgets a game removed from the library: its place in the queue and what the queue knew of its scan, so the same id imported again is scanned again. */
  forget(id: string): void;
  /** Seconds a game's scan takes on average in this session, for the progress estimate. */
  secondsPerGame(): number;
}
